/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Close, Save } from "@mui/icons-material";
import {
  Card,
  CardContent,
  Grid,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

import { Form, FormikProvider, useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import PageContainer from "@jumbo/components/PageContainer";
import LoadingButton from "@jumbo/components/JumboLoadingButton";
import moment from "moment";
import { AUDITS } from "app/routes/constant";
import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";
import { getProductList } from "app/redux/slice/productSlice";
import { getVariantList } from "app/redux/slice/variantSlice";

import { getFabricList } from "app/redux/slice/fabricSlice";
import { createAudit } from "app/redux/slice/auditSlice";

const useStyles = makeStyles({
  headingTitle: {
    minWidth: "80px",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "end",
    margin: "6px 0px",
  },
  margin: {
    "& > :first-child": {
      marginTop: "10px",
    },
    marginBottom: "10px",
  },
  alignTop: {
    verticalAlign: "top",
  },
  loadingButtonContainer: {
    display: "flex",
    justifyContent: "end",
    marginTop: "20px",
  },
  imageUploadContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
    padding: "5px",
    border: "2px dashed #ccc",
    borderRadius: "8px",
    minHeight: "120px",
    justifyContent: "center",
    cursor: "pointer",
    "&:hover": {
      borderColor: "#1976d2",
    },
  },
  imagePreview: {
    width: "80px",
    height: "80px",
    objectFit: "cover",
    borderRadius: "8px",
  },
  hiddenInput: {
    display: "none",
  },
});

const AddEditAudit = () => {
  const navigate = useNavigate();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const productData = useSelector((state) => state?.product);
  const variantData = useSelector((state) => state?.variant);
  const fabricData = useSelector((state) => state?.fabric);

  const mobileView = useMediaQuery("(max-width:575px)");

  const validationSchema = yup.object({
    type: yup
      .string()
      .oneOf(["product", "fabric"])
      .required("Please select a type."),
    date: yup.string().required("Please enter a date."),
    productId: yup.string().nullable(),
    variantId: yup.string().nullable(),
    fabricId: yup.string().nullable(),
    badQuantity: yup
      .number()
      .typeError("badQuantity must be a number")
      .min(0, "Must be greater than or equal to 0")
      .nullable(),
    missingQuantity: yup
      .number()
      .typeError("MissingQuantity must be a number")
      .min(0, "Must be greater than or equal to 0")
      .nullable(),
    newFoundQuantity: yup
      .number()
      .typeError("newFoundQuantity must be a number")
      .min(0, "Must be greater than or equal to 0")
      .nullable(),
  });

  const formik = useFormik({
    initialValues: {
      type: "product",
      date: moment(new Date()).format("YYYY-MM-DD"),
      productId: "",
      variantId: "",
      fabricId: "",
      badQuantity: 0,
      missingQuantity: 0,
      newFoundQuantity: 0,
    },
    validationSchema: validationSchema,
    validateOnChange: false,
    validate: (values) => {
      const errors = {};
      if (values?.type === "product") {
        const productVariants =
          variantData?.list?.filter(
            (data) => data?.productId === values?.productId
          ) ?? [];
        if (!values?.productId) {
          errors.productId = "Please select a product";
        }
        if (productVariants.length > 0 && !values?.variantId) {
          errors.variantId = "Please select a variant";
        }
      }
      if (values?.type === "fabric") {
        if (!values?.fabricId) {
          errors.fabricId = "Please enter/select a fabric";
        }
      }
      return errors;
    },
    onSubmit: async (values, { resetForm, setFieldError, setSubmitting }) => {
      const finalValues = Object.fromEntries(
        Object.entries(values).filter(([_, value]) => value !== "")
      );

      try {
        const response = await dispatch(createAudit(finalValues)).unwrap();

        if (response?.status === 200) {
          enqueueSnackbar(response.data?.responseMessage, {
            variant: "success",
          });
          navigate(AUDITS);
          resetForm();
        } else {
          enqueueSnackbar(response.data?.responseMessage, {
            variant: "error",
          });
        }
      } catch (error) {
        enqueueSnackbar(error?.data?.responseMessage, {
          variant: "error",
        });
      }
      setSubmitting(false);
    },
  });

  const { values, setFieldValue, errors } = formik;
  const { isSubmitting } = formik;

  const handleChange = (field, value) => {
    setFieldValue(field, value);
    if (field === "productId") {
      setFieldValue("variantId", "");
    }
    if (field === "type") {
      if (value === "product") {
        setFieldValue("fabricId", "");
      }
      if (value === "fabric") {
        setFieldValue("productId", "");
        setFieldValue("variantId", "");
      }
    }
  };

  useEffect(() => {
    dispatch(getProductList({ sort: 1, field: "name" }));
    dispatch(getVariantList());
    dispatch(getFabricList());
  }, []);

  return (
    <PageContainer heading={"Add Audit"}>
      <Card>
        <CardContent>
          <FormikProvider value={formik}>
            <Form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
              <Grid container spacing={2}>
                <Grid item sm={6} xs={12}>
                  <TextField
                    select
                    SelectProps={{ native: true }}
                    label={"Type*"}
                    name="type"
                    fullWidth
                    value={values?.type}
                    onChange={(evt) => handleChange("type", evt.target.value)}
                    error={Boolean(errors?.type)}
                    helperText={errors?.type}
                  >
                    <option value="product">Product</option>
                    <option value="fabric">Fabric</option>
                  </TextField>
                </Grid>
                <Grid item sm={6} xs={12}>
                  <TextField
                    label={"Date*"}
                    name="date"
                    fullWidth
                    type="date"
                    onChange={(evt) => handleChange("date", evt.target.value)}
                    InputLabelProps={{ shrink: true }}
                    value={values?.date}
                    error={Boolean(errors?.date)}
                    helperText={errors?.date}
                  />
                </Grid>
                {values?.type === "product" && (
                  <>
                    <Grid item sm={6} xs={12}>
                      <JumboAutoComplete
                        options={productData?.list ?? []}
                        label="Product*"
                        getOptionLabel={(option) => option?.name}
                        name={"productId"}
                        size="large"
                        value={
                          productData?.list?.find(
                            (data) => data?._id === values?.productId
                          ) || null
                        }
                        onChange={(evt, value) =>
                          handleChange("productId", value?._id)
                        }
                        helperText={errors?.productId}
                        isOptionEqualToValue={(option, value) =>
                          option?._id === value?._id
                        }
                      />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                      <JumboAutoComplete
                        options={
                          variantData?.list?.filter(
                            (data) => data?.productId === values?.productId
                          ) ?? []
                        }
                        size="large"
                        label={
                          (
                            variantData?.list?.filter(
                              (data) => data?.productId === values?.productId
                            ) || []
                          ).length > 0
                            ? "Variant*"
                            : "Variant"
                        }
                        getOptionLabel={(option) => option?.name}
                        name={"variantId"}
                        value={
                          values?.variantId
                            ? variantData?.list?.find(
                                (data) => data?._id === values?.variantId
                              ) || null
                            : null
                        }
                        onChange={(evt, value) =>
                          handleChange("variantId", value?._id)
                        }
                        helperText={errors?.variantId}
                        isOptionEqualToValue={(option, value) =>
                          option?._id === value?._id
                        }
                      />
                    </Grid>
                  </>
                )}
                {values?.type === "fabric" && (
                  <>
                    <Grid item sm={6} xs={12}>
                      <JumboAutoComplete
                        options={fabricData?.list ?? []}
                        label="Fabric*"
                        getOptionLabel={(option) => option?.name}
                        name={"fabricId"}
                        size="large"
                        value={
                          fabricData?.list?.find(
                            (data) => data?._id === values?.fabricId
                          ) || null
                        }
                        onChange={(evt, value) =>
                          handleChange("fabricId", value?._id)
                        }
                        helperText={errors?.fabricId}
                        isOptionEqualToValue={(option, value) =>
                          option?._id === value?._id
                        }
                      />
                    </Grid>
                    <Grid item sm={6} xs={12}></Grid>
                  </>
                )}
                <Grid item sm={4} xs={12}>
                  <TextField
                    label={"Bad Quantity"}
                    name="badQuantity"
                    fullWidth
                    type="number"
                    value={values?.badQuantity}
                    onChange={(evt) =>
                      handleChange("badQuantity", Number(evt.target.value))
                    }
                    error={Boolean(errors?.badQuantity)}
                    helperText={errors?.badQuantity}
                  />
                </Grid>
                <Grid item sm={4} xs={12}>
                  <TextField
                    label={"Missing Quantity"}
                    name="missingQuantity"
                    fullWidth
                    type="number"
                    value={values?.missingQuantity}
                    onChange={(evt) =>
                      handleChange("missingQuantity", Number(evt.target.value))
                    }
                    error={Boolean(errors?.missingQuantity)}
                    helperText={errors?.missingQuantity}
                  />
                </Grid>
                <Grid item sm={4} xs={12}>
                  <TextField
                    label={"New Found Quantity"}
                    name="newFoundQuantity"
                    fullWidth
                    type="number"
                    value={values?.newFoundQuantity}
                    onChange={(evt) =>
                      handleChange("newFoundQuantity", Number(evt.target.value))
                    }
                    error={Boolean(errors?.newFoundQuantity)}
                    helperText={errors?.newFoundQuantity}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={3}>
                <Grid
                  item
                  sm={12}
                  xs={12}
                  className={classes?.loadingButtonContainer}
                >
                  <LoadingButton
                    type="submit"
                    buttonLabel={"Save"}
                    sx={{ marginRight: "10px" }}
                    startIcon={<Save />}
                    variant="outlined"
                    fullWidth={mobileView}
                    loading={isSubmitting}
                  />

                  <LoadingButton
                    color={"secondary"}
                    buttonLabel={"Close"}
                    variant="outlined"
                    startIcon={<Close />}
                    onClick={() => navigate(AUDITS)}
                    fullWidth={mobileView}
                  />
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </Card>
    </PageContainer>
  );
};

export default AddEditAudit;
